import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import dayjs from "dayjs";
import { mainClient, getWebSocketReady } from "../apollo";
import authService from "../services/authService";
import { saveItemData, getItemInfo } from "../components/cache/itemData";
import { writeUserData } from "../components/cache/userData";
import { subscriptionEvents } from "../utils/subscriptionEvents";

// GraphQL imports
import { GET_ALL_ITEMINFO } from "../components/graphql/itemInfo";
import { GET_All_USER_DATA } from "../components/graphql/manageUser";

/**
 * DataManagerContext
 *
 * 로그인 후 백그라운드에서 데이터를 자동으로 로드하고,
 * realSign 구독을 통해 실시간 PnL을 업데이트하는 중앙 데이터 관리 Context
 *
 * 주요 기능:
 * 1. 로그인 감지 → Item/User 데이터 자동 로드
 * 2. Position Index로 O(1) 사용자 조회
 * 3. subscriptionEvents로 sign/currentState 이벤트 수신 → 영향받는 사용자만 PnL 재계산
 * 4. RAF 기반 배치 업데이트로 UI 성능 최적화
 */

// 초기 합계 데이터
const initialSumData = {
  liveProfitNLoss: 0,
  totValuationGNL: 0,
  profitNLoss: 0,
  totalFee: 0,
  liveUserBalance: 0,
};

// Context 생성
const DataManagerContext = createContext(null);

/**
 * 포지션 계산 함수 (Users 클래스 로직 기반)
 * 개별 사용자의 포지션에 대한 평가손익 계산
 */
const calculatePositions = (user) => {
  const { id, userName, position } = user;
  const result = [];

  for (let i = 0; i < position.length; i++) {
    const { itemCode, isBuy, isSigned, price, count } = position[i];

    const itemInfo = getItemInfo(itemCode);
    if (itemInfo === null) continue;

    const {
      short_hname,
      hname,
      unit_price: unitPrice,
      digit,
      tick_value: tickValue,
      real_sign: { price: currentPrice },
      real_hoga: {
        bidho1,
        bidho2,
        bidho3,
        bidho4,
        bidho5,
        offerho1,
        offerho2,
        offerho3,
        offerho4,
        offerho5,
      },
      currency_data: { value },
    } = itemInfo;

    if (isSigned) {
      const positionPrice = parseFloat(price);
      const valuationGNL = isBuy
        ? ((currentPrice - positionPrice) * value * tickValue) / unitPrice
        : ((positionPrice - currentPrice) * value * tickValue) / unitPrice;

      const itemName = short_hname || hname;

      const fixedPrice = positionPrice.toFixed(digit + 1).endsWith("0")
        ? positionPrice.toFixed(digit)
        : positionPrice.toFixed(digit + 1);
      const fixedCurrentPrice = currentPrice.toFixed(digit + 1).endsWith("0")
        ? currentPrice.toFixed(digit)
        : currentPrice.toFixed(digit + 1);
      const concludePrice = isBuy
        ? bidho1 || bidho2 || bidho3 || bidho4 || bidho5 || currentPrice
        : offerho1 || offerho2 || offerho3 || offerho4 || offerho5 || currentPrice;

      result.push({
        id,
        userName,
        ...position[i],
        price: fixedPrice,
        currentPrice: fixedCurrentPrice,
        valuationGNL: Math.round(valuationGNL) * count,
        hname: itemName,
        concludePrice,
      });
    } else {
      result.push({ ...position[i], id, userName });
    }
  }

  return result;
};

/**
 * 사용자 현재 포지션 기반 PnL 계산 (Users 클래스 로직 기반)
 */
const calculateCurrentPosition = (user) => {
  if (user === undefined) return null;
  const { position, userBalance, pureProfitNLoss, totalFee } = user;

  const result = { ...user, pureProfitNLoss, totalFee, userBalance };

  let totValuationGNL = 0;
  for (let i = 0; i < position.length; i++) {
    const { valuationGNL, isSigned } = position[i];
    if (isSigned) {
      totValuationGNL += parseInt(valuationGNL);
    }
  }

  // 실시간손익 = 실현손익(수수료 포함) + 평가손익
  result.liveProfitNLoss = pureProfitNLoss + totalFee + totValuationGNL;
  // 평가손익
  result.totValuationGNL = totValuationGNL;
  // 실현손익 (pureProfitNLoss + totalFee)
  result.profitNLoss = pureProfitNLoss + totalFee;
  // 평가담보금
  result.liveUserBalance = totValuationGNL + userBalance;

  return result;
};

/**
 * DataManagerProvider 컴포넌트
 *
 * DashBoard 컴포넌트 내부에서 사용되어야 합니다.
 * (이미 인증된 상태에서만 마운트됨)
 */
export const DataManagerProvider = ({ children }) => {
  // authService 사용하여 인증 상태 확인
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());

  // 데이터 준비 상태
  const [isDataReady, setIsDataReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 전체 유저 데이터 (Map<userId, UserData>)
  const allUsersRef = useRef(new Map());

  // PnL 데이터 (UI 소비용)
  const [userPnLData, setUserPnLData] = useState([]);
  const [sumData, setSumData] = useState(initialSumData);

  // Position Index (Map<itemCode, Set<userId>>)
  const positionIndexRef = useRef(new Map());

  // RAF 배치 업데이트 관리
  const pendingUpdatesRef = useRef(new Set());
  const rafIdRef = useRef(null);

  /**
   * Position Index 구축
   * itemCode → 해당 아이템을 보유한 userId Set
   */
  const buildPositionIndex = useCallback((users) => {
    const posIndex = new Map();

    users.forEach((user) => {
      if (!user.position) return;

      user.position.forEach((pos) => {
        if (!pos.isSigned) return; // 체결된 포지션만

        if (!posIndex.has(pos.itemCode)) {
          posIndex.set(pos.itemCode, new Set());
        }
        posIndex.get(pos.itemCode).add(user.id);
      });
    });

    positionIndexRef.current = posIndex;
    return posIndex;
  }, []);

  /**
   * 합계 재계산 (모의투자 제외)
   */
  const recalculateSum = useCallback(() => {
    let sumLiveProfitNLoss = 0;
    let sumTotValuationGNL = 0;
    let sumProfitNLoss = 0;
    let sumTotalFee = 0;
    let sumLiveUserBalance = 0;

    allUsersRef.current.forEach((user) => {
      const { authority } = user;
      // 준회원(0), 정회원(1)만 합산, 모의투자(4)는 제외
      if (authority === 1 || authority === 0) {
        sumLiveProfitNLoss += user.liveProfitNLoss || 0;
        sumTotValuationGNL += user.totValuationGNL || 0;
        sumProfitNLoss += user.profitNLoss || 0;
        sumTotalFee += user.totalFee || 0;
        sumLiveUserBalance += user.liveUserBalance || 0;
      }
    });

    setSumData({
      liveProfitNLoss: sumLiveProfitNLoss,
      totValuationGNL: sumTotValuationGNL,
      profitNLoss: sumProfitNLoss,
      totalFee: sumTotalFee,
      liveUserBalance: sumLiveUserBalance,
    });
  }, []);

  /**
   * 특정 사용자들의 PnL 재계산
   */
  const calculatePnLForUsers = useCallback((userIds) => {
    const updatedUsers = [];

    userIds.forEach((userId) => {
      const userData = allUsersRef.current.get(userId);
      if (!userData) return;

      // 포지션 계산
      const calculatedPositions = calculatePositions(userData);
      // PnL 계산
      const pnlData = calculateCurrentPosition({
        ...userData,
        position: calculatedPositions,
      });

      if (pnlData) {
        // allUsers 업데이트
        allUsersRef.current.set(userId, pnlData);
        updatedUsers.push(pnlData);
      }
    });

    // State 업데이트
    setUserPnLData(Array.from(allUsersRef.current.values()));

    // 합계 재계산
    recalculateSum();
  }, [recalculateSum]);

  /**
   * 전체 사용자 PnL 계산
   */
  const calculateAllPnL = useCallback(() => {
    const allUserIds = Array.from(allUsersRef.current.keys());
    calculatePnLForUsers(allUserIds);
  }, [calculatePnLForUsers]);

  /**
   * RAF 기반 배치 업데이트 스케줄러
   */
  const scheduleUpdate = useCallback(() => {
    if (rafIdRef.current) return;

    rafIdRef.current = requestAnimationFrame(() => {
      const userIds = Array.from(pendingUpdatesRef.current);
      pendingUpdatesRef.current.clear();
      rafIdRef.current = null;

      if (userIds.length > 0) {
        calculatePnLForUsers(userIds);
      }
    });
  }, [calculatePnLForUsers]);

  /**
   * 특정 사용자의 Position Index 갱신
   */
  const rebuildPositionIndexForUser = useCallback((userId, newPositions) => {
    // 기존 인덱스에서 해당 유저 제거
    positionIndexRef.current.forEach((userSet) => {
      userSet.delete(userId);
    });

    // 새 포지션으로 인덱스 재구축
    if (newPositions) {
      newPositions.forEach((pos) => {
        if (!pos.isSigned) return;

        if (!positionIndexRef.current.has(pos.itemCode)) {
          positionIndexRef.current.set(pos.itemCode, new Set());
        }
        positionIndexRef.current.get(pos.itemCode).add(userId);
      });
    }
  }, []);

  /**
   * sign 이벤트 핸들러 (SubscriptionContext → subscriptionEvents 경유)
   */
  const handleSignEvent = useCallback((signs) => {
    const signArray = Array.isArray(signs) ? signs : [signs];
    if (signArray.length === 0) return;

    // 영향받는 유저 수집 (O(1) 조회)
    const affectedUsers = new Set();
    signArray.forEach((sign) => {
      if (!sign?.code) return;
      const users = positionIndexRef.current.get(sign.code);
      if (users) {
        users.forEach((id) => affectedUsers.add(id));
      }
    });

    // 배치 업데이트 예약
    if (affectedUsers.size > 0) {
      affectedUsers.forEach((id) => pendingUpdatesRef.current.add(id));
      scheduleUpdate();
    }
  }, [scheduleUpdate]);

  /**
   * currentState 이벤트 핸들러 (SubscriptionContext → subscriptionEvents 경유)
   */
  const handleCurrentStateEvent = useCallback((stateData) => {
    if (!stateData) return;

    const { id: userId } = stateData;

    // allUsers 업데이트
    const existingUser = allUsersRef.current.get(userId);
    if (existingUser) {
      const updatedUser = {
        ...existingUser,
        position: stateData.position,
        userBalance: stateData.userBalance,
        authority: stateData.authority,
      };

      if (stateData.user_balance_history && stateData.user_balance_history[0]) {
        const { pureProfitNLoss, totalFee, userBalance } =
          stateData.user_balance_history[0];
        updatedUser.pureProfitNLoss = pureProfitNLoss;
        updatedUser.totalFee = totalFee;
        updatedUser.userBalance = userBalance;
      }

      allUsersRef.current.set(userId, updatedUser);
      rebuildPositionIndexForUser(userId, stateData.position);
      pendingUpdatesRef.current.add(userId);
      scheduleUpdate();
    } else {
      // 신규 사용자
      const balanceHistory = stateData.user_balance_history?.[0] || {};
      const newUser = {
        id: userId,
        userName: stateData.userName || userId,
        recommendCode: stateData.recommendCode || "",
        position: stateData.position,
        userBalance: balanceHistory.userBalance || stateData.userBalance || 0,
        authority: stateData.authority,
        pureProfitNLoss: balanceHistory.pureProfitNLoss || 0,
        totalFee: balanceHistory.totalFee || 0,
        profitNLoss:
          (balanceHistory.pureProfitNLoss || 0) +
          (balanceHistory.totalFee || 0),
        totValuationGNL: 0,
        liveProfitNLoss: 0,
        liveUserBalance: 0,
      };
      allUsersRef.current.set(userId, newUser);
      rebuildPositionIndexForUser(userId, stateData.position);
      pendingUpdatesRef.current.add(userId);
      scheduleUpdate();
    }
  }, [scheduleUpdate, rebuildPositionIndexForUser]);

  /**
   * 초기 데이터 로드
   */
  const loadInitialData = useCallback(async () => {
    console.log("🔄 [DataManager] 초기 데이터 로드 시작");
    setIsLoading(true);
    setError(null);

    try {
      // WebSocket 준비 대기
      await getWebSocketReady();

      // 1. Item 데이터 로드
      console.log("📦 [DataManager] Item 데이터 로드 중...");
      const { data: itemData } = await mainClient.query({
        query: GET_ALL_ITEMINFO,
        fetchPolicy: "network-only",
      });

      itemData.getAllItemInfo.forEach((item) => saveItemData(item));
      console.log(`✅ [DataManager] Item ${itemData.getAllItemInfo.length}개 로드 완료`);

      // 2. User 데이터 로드
      console.log("👥 [DataManager] User 데이터 로드 중...");
      const { data: userData } = await mainClient.query({
        query: GET_All_USER_DATA,
        variables: { withTester: true },
        fetchPolicy: "network-only",
      });

      // Apollo Cache에 저장 (기존 시스템 호환)
      writeUserData(userData.getAllUserData);

      // 3. 전체 사용자 포함 (비활성 사용자는 positionIndex에 포함되지 않아 성능 영향 없음)
      const allUserData = userData.getAllUserData;

      console.log(
        `✅ [DataManager] User ${allUserData.length}명 로드 완료`
      );

      // 4. 전체 유저 Map 구축
      const usersMap = new Map();
      allUserData.forEach((user) => {
        // user_balance_history에서 필요한 데이터 추출
        const balanceHistory = user.user_balance_history?.[0] || {};
        const today = dayjs();
        const referenceTime = dayjs().hour(7).minute(40);
        let todayDateId;
        if (today.isBefore(referenceTime)) {
          todayDateId = parseInt(today.subtract(1, "day").format("YYYYMMDD"));
        } else {
          todayDateId = parseInt(today.format("YYYYMMDD"));
        }

        const processedUser = {
          ...user,
          totalFee: balanceHistory.dateId === todayDateId ? balanceHistory.totalFee || 0 : 0,
          pureProfitNLoss:
            balanceHistory.dateId === todayDateId ? balanceHistory.pureProfitNLoss || 0 : 0,
          profitNLoss:
            balanceHistory.dateId === todayDateId ? balanceHistory.profitNLoss || 0 : 0,
          userBalance: balanceHistory.userBalance || user.userBalance || 0,
          totValuationGNL: 0,
          liveProfitNLoss: 0,
          liveUserBalance: 0,
        };

        usersMap.set(user.id, processedUser);
      });

      allUsersRef.current = usersMap;

      // 5. Position Index 구축
      buildPositionIndex(allUserData);
      console.log(
        `✅ [DataManager] Position Index 구축 완료 (${positionIndexRef.current.size}개 아이템)`
      );

      // 6. 초기 PnL 계산
      calculateAllPnL();

      setIsDataReady(true);
      console.log("✅ [DataManager] 초기 데이터 로드 완료");
    } catch (err) {
      console.error("❌ [DataManager] 초기 데이터 로드 실패:", err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [buildPositionIndex, calculateAllPnL]);

  /**
   * 데이터 리셋
   */
  const resetData = useCallback(() => {
    console.log("🔄 [DataManager] 데이터 리셋");

    // RAF 취소
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    // 상태 초기화
    allUsersRef.current = new Map();
    positionIndexRef.current = new Map();
    pendingUpdatesRef.current = new Set();

    setUserPnLData([]);
    setSumData(initialSumData);
    setIsDataReady(false);
    setIsLoading(false);
    setError(null);
  }, []);

  /**
   * 특정 유저 Lazy Loading (캐시에서 유저 조회 시)
   */
  const loadUserOnDemand = useCallback(
    async (userId) => {
      if (allUsersRef.current.has(userId)) {
        return allUsersRef.current.get(userId);
      }

      console.log(`📥 [DataManager] On-demand 유저 로드: ${userId}`);

      // 캐시에서 먼저 확인
      const cachedUser = mainClient.readFragment({
        id: `USERDATA:${userId}`,
        fragment: require("../components/cache/userDataGql").USER_DATA,
      });

      if (cachedUser) {
        // allUsers에 추가
        allUsersRef.current.set(userId, cachedUser);

        // Position Index 업데이트
        rebuildPositionIndexForUser(userId, cachedUser.position);

        // PnL 계산
        pendingUpdatesRef.current.add(userId);
        scheduleUpdate();

        return cachedUser;
      }

      return null;
    },
    [rebuildPositionIndexForUser, scheduleUpdate]
  );

  // 컴포넌트 마운트 시 초기 데이터 로드
  // (DashBoard 내부에서 마운트되므로 이미 인증된 상태)
  useEffect(() => {
    if (!isDataReady && !isLoading) {
      loadInitialData();
    }

    return () => {
      // 언마운트 시 데이터 리셋
      resetData();
    };
  }, []); // 의도적으로 빈 의존성 배열 - 마운트 시 1회만 실행

  // subscriptionEvents 이벤트 구독 (데이터 준비 후)
  // SubscriptionContext가 WebSocket 구독을 관리하고, subscriptionEvents로 이벤트를 발행
  // DataManager는 이벤트만 받아서 PnL 계산에 집중
  useEffect(() => {
    if (!isDataReady) return;

    console.log("🔔 [DataManager] subscriptionEvents 이벤트 구독 시작");

    const cleanupSign = subscriptionEvents.on('sign', handleSignEvent);
    const cleanupCurrentState = subscriptionEvents.on('currentState', handleCurrentStateEvent);

    return () => {
      console.log("🔕 [DataManager] subscriptionEvents 이벤트 구독 해제");
      cleanupSign();
      cleanupCurrentState();
    };
  }, [isDataReady, handleSignEvent, handleCurrentStateEvent]);

  // Context value
  const value = {
    // 상태
    isDataReady,
    isLoading,
    error,

    // PnL 데이터 (UI 소비용)
    userPnLData,
    sumData,

    // 유틸리티 함수
    loadUserOnDemand,
    refreshData: loadInitialData,
  };

  return (
    <DataManagerContext.Provider value={value}>
      {children}
    </DataManagerContext.Provider>
  );
};

/**
 * useDataManager Hook
 * DataManagerContext에 접근하기 위한 기본 훅
 */
export const useDataManager = () => {
  const context = useContext(DataManagerContext);
  if (!context) {
    throw new Error("useDataManager must be used within DataManagerProvider");
  }
  return context;
};

/**
 * useUserPnL Hook
 * PnL 데이터만 필요한 컴포넌트용 편의 훅
 */
export const useUserPnL = () => {
  const { userPnLData, sumData, isDataReady, isLoading } = useDataManager();
  return {
    userPnLData,
    sumData,
    isDataReady,
    isLoading,
  };
};

/**
 * useDataReady Hook
 * 데이터 준비 상태만 확인하는 훅
 */
export const useDataReady = () => {
  const { isDataReady, isLoading, error } = useDataManager();
  return { isDataReady, isLoading, error };
};

export default DataManagerProvider;
